import { Link } from 'react-router-dom';
import type { BauvorhabenRechnungen } from '@/types';
import { formatCurrency } from '@/utils/currency';
import { formatDateOnly } from '@/utils/progress';
import {
  invoiceStatusLabels, invoiceStatusBadgeClasses, paymentStatusLabels,
} from '@/components/rechnungen/invoiceStatus';
import { Receipt, AlertTriangle } from 'lucide-react';

/**
 * Finanz-Karte im Bauvorhaben-Detail (unter den Gewerken): Summen der
 * Eingangsrechnungen + die letzten Rechnungen als Links in die
 * Rechnungen-Spalte. Die Daten liefert die Detail-Layoutroute — die Karte
 * selbst ist zustandslos.
 */
export function BauvorhabenRechnungenCard({ data }: { data: BauvorhabenRechnungen | null }) {
  if (!data) {
    return (
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Receipt className="h-5 w-5 text-gray-400" />
          Finanzen
        </h2>
        <p className="text-sm text-gray-500">Rechnungen konnten nicht geladen werden.</p>
      </div>
    );
  }

  const { summary, invoices } = data;
  const recent = invoices.slice(0, 8);

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Receipt className="h-5 w-5 text-gray-400" />
          Finanzen
        </h2>
        <Link to="/rechnungen" className="text-sm text-primary-600 hover:text-primary-700">
          Alle Rechnungen
        </Link>
      </div>

      {/* Summen (Strings aus DECIMAL — formatCurrency übernimmt) */}
      <div className="grid gap-3 sm:grid-cols-3 mb-5">
        <div className="rounded-lg bg-gray-50 px-4 py-3">
          <p className="text-xs text-gray-500">Offen (Eingang)</p>
          <p className="mt-0.5 text-lg font-semibold text-gray-900">{formatCurrency(summary.sum_offen)}</p>
          <p className="text-xs text-gray-500">
            {summary.count_offen} Rechnungen
            {summary.count_offen_overdue > 0 && (
              <span className="ml-1 inline-flex items-center gap-0.5 text-red-600">
                <AlertTriangle className="h-3 w-3" />
                {summary.count_offen_overdue} überfällig
              </span>
            )}
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 px-4 py-3">
          <p className="text-xs text-gray-500">Freigegeben</p>
          <p className="mt-0.5 text-lg font-semibold text-gray-900">{formatCurrency(summary.sum_freigegeben)}</p>
        </div>
        <div className="rounded-lg bg-gray-50 px-4 py-3">
          <p className="text-xs text-gray-500">Gesamt</p>
          <p className="mt-0.5 text-lg font-semibold text-gray-900">{formatCurrency(summary.sum_gesamt)}</p>
        </div>
      </div>

      {recent.length === 0 ? (
        <p className="text-sm text-gray-500">Noch keine Rechnungen für dieses Bauvorhaben.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {recent.map((invoice) => (
            <li key={invoice.id}>
              <Link to={`/rechnungen/${invoice.id}`} className="flex items-center justify-between gap-3 py-2 hover:text-primary-700">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900">{invoice.invoice_number}</span>
                  <span className="block truncate text-xs text-gray-500">
                    {[invoice.contact_name, formatDateOnly(invoice.invoice_date)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(invoice.amount_gross)}</span>
                  <span className={`hidden sm:inline-block rounded-full px-2 py-0.5 text-xs font-medium ${invoiceStatusBadgeClasses[invoice.status]}`}>
                    {invoiceStatusLabels[invoice.status]}
                  </span>
                  {invoice.payment_status === 'bezahlt' && (
                    <span className="hidden sm:inline-block rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                      {paymentStatusLabels.bezahlt}
                    </span>
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}